import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  EnvelopeSimple, User, Phone, Buildings, ChatText, PaperPlaneTilt
} from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  'Agentic AI',
  'AI-Powered DevSecOps',
  'CI/CD Automation',
  'QA Testing Suites',
  'Cloud & FinOps',
  'Infrastructure & Compute',
  'Other',
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', service_interest: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to submit. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <div className="pt-[72px]">
        {/* Header */}
        <div className="bg-[#0B132B] py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#10B981] mb-3">Contact Us</p>
            <h1
              className="text-4xl sm:text-5xl tracking-tighter font-black text-white"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Consult a Solutions Architect
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mt-4 max-w-2xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Tell us about your requirements and our team will craft a tailored enterprise solution for your organization.
            </p>
          </div>
        </div>

        {/* Form area */}
        <div className="max-w-4xl mx-auto px-6 lg:px-16 -mt-10">
          {submitted ? (
            <div className="bg-white border border-slate-200 p-12 text-center" data-testid="contact-success">
              <PaperPlaneTilt size={48} weight="duotone" className="text-[#10B981] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#0B132B] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Inquiry Submitted
              </h2>
              <p className="text-base text-[#475569] mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Our solutions architecture team will reach out within 24 hours.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="rounded-none bg-[#0052FF] hover:bg-[#0040CC] text-white"
                data-testid="contact-back-home-btn"
              >
                Back to Home
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 p-8 lg:p-10" data-testid="contact-form-container">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-base font-medium text-[#0B132B]">Full Name *</Label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        name="name" value={form.name} onChange={handleChange} required
                        placeholder="John Doe"
                        className="pl-10 h-11 rounded-none border-slate-200 focus-visible:ring-[#0052FF]"
                        data-testid="contact-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-medium text-[#0B132B]">Email *</Label>
                    <div className="relative">
                      <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        name="email" type="email" value={form.email} onChange={handleChange} required
                        placeholder="you@company.com"
                        className="pl-10 h-11 rounded-none border-slate-200 focus-visible:ring-[#0052FF]"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-base font-medium text-[#0B132B]">Company</Label>
                    <div className="relative">
                      <Buildings size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        name="company" value={form.company} onChange={handleChange}
                        placeholder="Acme Corp"
                        className="pl-10 h-11 rounded-none border-slate-200 focus-visible:ring-[#0052FF]"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-medium text-[#0B132B]">Phone</Label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        name="phone" value={form.phone} onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                        className="pl-10 h-11 rounded-none border-slate-200 focus-visible:ring-[#0052FF]"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-medium text-[#0B132B]">Service Interest</Label>
                  <select
                    name="service_interest"
                    value={form.service_interest}
                    onChange={handleChange}
                    className="flex h-11 w-full border border-slate-200 bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0052FF]"
                    data-testid="contact-service-select"
                  >
                    <option value="">Select a service</option>
                    {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-medium text-[#0B132B]">Message *</Label>
                  <div className="relative">
                    <ChatText size={18} className="absolute left-3 top-3 text-slate-400" />
                    <textarea
                      name="message" value={form.message} onChange={handleChange} required
                      placeholder="Describe your requirements..."
                      rows={5}
                      className="flex w-full border border-slate-200 bg-transparent pl-10 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0052FF] resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-none bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </form>
            </div>
          )}
        </div>

        <div className="mt-24">
          <Footer />
        </div>
      </div>
    </div>
  );
}
